import { useState, useEffect } from 'react';
import { graphql } from 'react-relay';
import get from 'lodash.get';
import tracking from 'dibs-tracking';
import md5 from 'md5';
import { getEverLoggedIn, getGuestId, getBuyerId } from 'dibs-cookie-jar';
import { localStorage, sessionStorage } from 'dibs-browser-storage';
import { unmaskFragmentData } from '../helpers/relayUnmaskingHelper';
import { getIncomeLevelInfo } from './userGeoInfo';
import { setGuestId } from '../authModal/authentication/userStorageActions';

const USER_EMAIL_TOKEN = 'userEmailToken';
const EMAIL_TOKEN = 'emailToken';
const USER_EMAIL = 'userEmail';
const STORAGE_TRANSACTIONS_STATUS = 'userTransactionsStatus';
const STORAGE_CHECKOUT_STATUS = 'userCheckoutStatus';
const BUYER_STATUS_NONE = 'NONE';
const BUYER_STATUS_CONFIRMED_MULTIPLE = 'CONFIRMED_MULTIPLE';
const BUYER_STATUS_CONFIRMED_ONE = 'CONFIRMED_ONE';
const BUYER_STATUS_SUBMITTED = 'SUBMITTED';
const BUYER_STATUSES_MAP = {
    [BUYER_STATUS_CONFIRMED_MULTIPLE]: 'submitted|multiple confirmed',
    [BUYER_STATUS_CONFIRMED_ONE]: 'submitted|one confirmed',
    [BUYER_STATUS_SUBMITTED]: 'submitted|none confirmed',
    [BUYER_STATUS_NONE]: 'no orders',
};
export const CUSTOMER_TYPE_TRADE = 'Trade';
export const CUSTOMER_TYPE_VIP = 'VIP';
export const CUSTOMER_TYPE_CONSUMER = 'Consumer';
export const CUSTOMER_TYPE_UNKNOWN = 'Unknown';

/*
export type TrackingConfig = {|
    action: string,
    category: string,
    label?: string,
    value?: string,
    noninteraction?: boolean,
    additional?: {
        [key: string]: string,
    },
|};
*/

/**
 * Custom hook to wrap firing dibs-tracking's trackEvent
 * @param {Object} trackingConfig - Payload to trackEvent.
 * @param {boolean} [shouldFireTracking=true] - If the tracked event should fire by default.
 * @returns {function(): void} - Function to reset the tracker.
 */
export function useTrackEvent(trackingConfig, shouldFireTracking = true) {
    const [hasTrackedEvent, setHasTrackedEvent] = useState(false);
    useEffect(
        () => {
            if (!hasTrackedEvent && shouldFireTracking) {
                tracking.trackEvent(trackingConfig);
                setHasTrackedEvent(true);
            }
        },
        [shouldFireTracking]
    );
    return () => {
        setHasTrackedEvent(false);
    };
}

/**
 * Formats date string to MM-YYYY used by GA.
 */
export function formatDate(dateString) {
    if (dateString) {
        const date = new Date(dateString);

        if (!isNaN(date.getTime())) {
            return `${date.getMonth() + 1}-${date.getFullYear()}`;
        }
    }

    return undefined;
}

/*
 * depending on transaction history, determine which value to add for the BUYER_STATUSES_MAP
 */
function getBuyerStatusFromTransactions(transactions) {
    const { submittedTransactions, confirmedTransactions } = transactions;
    let buyerStatus;

    if (confirmedTransactions && confirmedTransactions.totalResults > 1) {
        buyerStatus = BUYER_STATUS_CONFIRMED_MULTIPLE;
    } else if (confirmedTransactions && confirmedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_CONFIRMED_ONE;
    } else if (submittedTransactions && submittedTransactions.totalResults) {
        buyerStatus = BUYER_STATUS_SUBMITTED;
    } else {
        buyerStatus = BUYER_STATUS_NONE;
    }

    return { buyerStatus };
}

/*
 * fetches all transactions created by user using different filters
 * in order to track order history
 */
export function getTransactionsStatus({ transactions = {}, resetStorage = false }) {
    let transactionsStatus = sessionStorage.getItem(STORAGE_TRANSACTIONS_STATUS);

    /**
     * Transactions status is always being taken from a session storage.
     * When user logs in or registers, this storage entry is set again.
     */
    if (!transactionsStatus || resetStorage) {
        transactionsStatus = getBuyerStatusFromTransactions(transactions);

        sessionStorage.setItem(STORAGE_TRANSACTIONS_STATUS, { ...transactionsStatus });
        localStorage.setItem(STORAGE_TRANSACTIONS_STATUS, { ...transactionsStatus });
    }

    if (transactionsStatus) {
        transactionsStatus.buyerStatus = BUYER_STATUSES_MAP[transactionsStatus.buyerStatus];
    }

    return transactionsStatus;
}

/*
 *   get current account type, track it on modal change (close) event
 */
export function getCurrentAccountType({
    user,
    passwordAdded,
    accountCreated,
    loggedIn,
    socialLogin,
}) {
    const emailOnly = get(user, 'authMetadata.emailOnly');
    const hasPassword = get(user, 'authMetadata.hasPassword');
    let currentAccountType;
    if (emailOnly || accountCreated) {
        currentAccountType = 'email registration';
    } else if (hasPassword || passwordAdded || loggedIn || socialLogin) {
        currentAccountType = 'email and password registration';
    }
    return currentAccountType;
}

/**
 * gets stored transactions status for logged out users
 */
function getStoredTransactionsStatus() {
    const transactionsStatus =
        sessionStorage.getItem(STORAGE_TRANSACTIONS_STATUS) ||
        localStorage.getItem(STORAGE_TRANSACTIONS_STATUS);
    if (transactionsStatus && transactionsStatus.buyerStatus) {
        transactionsStatus.buyerStatus = BUYER_STATUSES_MAP[transactionsStatus.buyerStatus];
        return transactionsStatus;
    } else {
        return {
            buyerStatus: BUYER_STATUSES_MAP[BUYER_STATUS_NONE],
        };
    }
}

/*
 * checkoutStatus logic is identical to buyer status, except it is
 * updated on different events and has nothing to do with session storage
 */
export function getCheckoutStatus({ transactions = {} }) {
    const storedCheckoutStatus = localStorage.getItem(STORAGE_CHECKOUT_STATUS);

    //  is user logged in? - only logged in user have 'transactions'
    if (typeof get(transactions, 'confirmedTransactions.totalResults') === 'number') {
        const checkoutStatus =
            BUYER_STATUSES_MAP[getBuyerStatusFromTransactions(transactions).buyerStatus];
        if (storedCheckoutStatus !== checkoutStatus) {
            localStorage.setItem(STORAGE_CHECKOUT_STATUS, checkoutStatus);
        }
        return checkoutStatus;
    } else {
        return storedCheckoutStatus || BUYER_STATUSES_MAP[BUYER_STATUS_NONE];
    }
}

/*
 * create the userType tag according to user permissions.
 * VIP|Trade|Consumer|Unknown
 */
export function getCustomerType(user) {
    if (user.serviceId) {
        const isTrade = user.isTrade ? CUSTOMER_TYPE_TRADE : '';
        const hasVipPermission = user.hasVipPermission ? CUSTOMER_TYPE_VIP : '';
        return (
            `${isTrade}${isTrade && hasVipPermission ? ' and ' : ''}${hasVipPermission}` ||
            CUSTOMER_TYPE_CONSUMER
        );
    } else if (user.everLoggedIn) {
        switch (user.everLoggedIn) {
            case 'trade':
                return CUSTOMER_TYPE_TRADE;
            case 'vip':
                return CUSTOMER_TYPE_VIP;
            default:
                return CUSTOMER_TYPE_CONSUMER;
        }
    } else {
        return CUSTOMER_TYPE_UNKNOWN;
    }
}

/**
 * @returns {(string|null)} - first ga session date for user
 */
export function getFirstUtmSessionDate(user = {}) {
    if (user.firstSessionDate) {
        return user.firstSessionDate;
    }

    if (typeof document.cookie !== 'string') {
        return null;
    }

    const match = document.cookie.match(/__utma=\d+\.\d+\.(\d+)/);
    if (match && match[1]) {
        return formatDate(match[1] * 1000);
    } else {
        return null;
    }
}

function getEmailTokenFromQuery() {
    return new URLSearchParams(location.search).get(EMAIL_TOKEN);
}

function getEmailToken() {
    return getEmailTokenFromQuery() || localStorage.getItem(USER_EMAIL_TOKEN);
}

function getUserIdFromEmailToken(emailToken) {
    if (emailToken) {
        return emailToken.split('_')[0];
    } else {
        return undefined;
    }
}

/*
 * creates the user tracking data.
 */
function trackUserAttributes({ transactions = {}, user = {}, regionalInfo }) {
    const emailToken = getEmailToken();
    const incomeRange = getIncomeLevelInfo(regionalInfo);
    const userID = get(user, 'serviceId') || getUserIdFromEmailToken(emailToken);
    const userEmail = get(user, 'profile.email') || localStorage.getItem(USER_EMAIL);
    const transactionsStatus = get(user, 'serviceId')
        ? getTransactionsStatus({ transactions })
        : getStoredTransactionsStatus();

    tracking.updateGtmPageInfo({
        checkoutStatus: getCheckoutStatus({ transactions }),
    });

    tracking.addUserAttributes({
        loginStatus: user.serviceId ? 'Logged In' : 'Logged Out',
        registrationStatus: user.everLoggedIn ? 'Registered' : 'Not Registered',
        userID,
        guestID: getGuestId(document.cookie),
        userEmail: userEmail ? md5(userEmail) : undefined,
        userEmailAddress: user.serviceId ? userEmail : undefined,
        emailToken,
        userZip: get(user, 'profile.zipCode'),
        defaultCurrency: get(user, 'profile.currency'),
        registrationDate: formatDate(user.createdDate),
        currentAccountType: getCurrentAccountType({ user }),
        firstSessionDate: getFirstUtmSessionDate(user) || undefined,
        contactDealerStatus: user.totalMessagesToDealers
            ? 'dealer contacted'
            : 'no dealer contacted',
        buyerStatus: transactionsStatus.buyerStatus,
        tradeStatus: user.verifiedTradeStatus,
        customerType: getCustomerType(user),
        tradeTier: get(user, 'tradeFirm.tier'),
        tradeFirmId: get(user, 'tradeFirm.serviceId'),
        tradeRewardStatus: get(user, 'tradeFirm.rewards.currentProgram.programInfo.code'),
        profileStatus:
            get(user, 'tradeFirm.profileStatus') === 'ACTIVE' ? 'PUBLISHED' : 'UNPUBLISHED',
        incomeRange,
    });
}

export const userFragment = graphql`
    fragment userTracking_user on User {
        createdDate
        firstSessionDate
        isTrade: hasPermission(name: "B_VERIFIED_TRADE")
        hasVipPermission: hasPermission(name: "B_VIP_CHECKOUT")
        authMetadata {
            emailOnly
            hasPassword
        }
        profile {
            email
            zipCode
            currency
        }
        serviceId
        totalMessagesToDealers
        tradeFirm {
            serviceId
            tier
            profileStatus
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                }
            }
        }
        verifiedTradeStatus
    }
`;

function getUserFragmentData(user) {
    const {
        firstSessionDate,
        isTrade,
        hasVipPermission,
        profile,
        serviceId,
        totalMessagesToDealers,
        tradeFirm,
        verifiedTradeStatus,
        createdDate,
        authMetadata,
    } = user;
    return {
        firstSessionDate,
        isTrade,
        hasVipPermission,
        profile,
        serviceId,
        totalMessagesToDealers,
        tradeFirm,
        verifiedTradeStatus,
        createdDate,
        authMetadata,
    };
}

export const viewerFragment = graphql`
    fragment userTracking_viewer on Viewer {
        submittedTransactions: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.submitted"
            first: 0
        ) @include(if: $hasUserId) {
            totalResults
        }
        confirmedTransactionsMostRecent: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.confirmed"
            first: 0
        ) @include(if: $hasUserId) {
            totalResults
        }
    }
`;

export const regionalInfoFragment = graphql`
    fragment userTracking_regionalInfo on RegionalInfo {
        incomeLevel(zipCode: $zipCode) {
            incomeBracket
            incomePercentile
        }
    }
`;

function getViewerFragmentData(viewer) {
    const { submittedTransactions, confirmedTransactionsMostRecent } = viewer;
    return {
        submittedTransactions,
        confirmedTransactions: confirmedTransactionsMostRecent,
    };
}

export function unmaskViewerFragmentData({ relayContext, viewer }) {
    const viewerData =
        unmaskFragmentData({
            relayContext: relayContext,
            relayData: viewer,
            fragmentPropName: 'viewer',
            fragment: viewerFragment,
        }) || {};
    return getViewerFragmentData(viewerData);
}

/*
 * should be called on initial page load for all entries.
 * determines user tracking data to send to dibs-tracking.
 */
export function init({ viewer = {}, user }) {
    const transactions = getViewerFragmentData(viewer);
    const { regionalInfo } = viewer;
    const everLoggedIn = getEverLoggedIn(document.cookie);
    const userFragmentData = user ? getUserFragmentData(user) : undefined;

    const queryEmailToken = getEmailTokenFromQuery();

    if (queryEmailToken) {
        localStorage.setItem(USER_EMAIL_TOKEN, queryEmailToken);
    }

    if (!getBuyerId(document.cookie) && !getGuestId(document.cookie)) {
        setGuestId();
    }

    trackUserAttributes({
        user: { ...userFragmentData, everLoggedIn },
        transactions,
        regionalInfo,
    });
}
